import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Hash, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ThreadCard from "@/components/ThreadCard";

const field = "w-full rounded-sm border border-[#2A2E37] bg-white/5 px-4 py-2.5 text-sm outline-none focus:border-white/40";

function CreateCommunity({ onCreated }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/communities", form);
      setOpen(false);
      onCreated(data);
      nav(`/c/${data.slug}`);
    } catch (err) { toast.error(formatErr(err)); }
  };
  return (
    <>
      <button onClick={() => (user ? setOpen(true) : nav("/login"))} data-testid="community-create-button"
        className="btn-neon flex items-center gap-2 rounded-sm px-5 py-2.5 text-sm font-semibold text-white">
        <Plus className="h-4 w-4" /> New community
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md border-[#2A2E37] bg-[#121418] text-white" data-testid="create-community-dialog">
          <DialogHeader><DialogTitle className="font-display text-lg font-semibold">Start a community</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <input className={field} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="community-name-input" />
            <textarea className={`${field} min-h-[90px]`} placeholder="What's it about?" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="community-description-input" />
            <button type="submit" data-testid="community-create-submit-btn" className="btn-neon w-full rounded-sm py-3 font-semibold text-white">Create</button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function Communities() {
  const [params, setParams] = useSearchParams();
  const about = params.get("about");
  const [communities, setCommunities] = useState([]);
  const [threads, setThreads] = useState([]);

  useEffect(() => { api.get("/communities").then((r) => setCommunities(r.data)); }, []);
  useEffect(() => { api.get("/threads", { params: { sort: "hot", about: about || undefined } }).then((r) => setThreads(r.data)); }, [about]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-16">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="font-mono text-[11px] font-medium uppercase tracking-[0.3em] text-gray-300">Communities</p>
          <h1 className="mt-2 font-display text-2xl font-semibold tracking-tight sm:text-3xl">The after-party</h1>
          <p className="mt-3 max-w-lg text-sm text-gray-400 md:text-base">Talk about creators, drops and nights out. Upvote what matters.</p>
        </div>
        <CreateCommunity onCreated={(c) => setCommunities((x) => [c, ...x])} />
      </div>
      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4" data-testid="threads-list">
          {about && (
            <div className="flex items-center gap-2 text-sm text-gray-300" data-testid="about-filter">
              Discussions about <span className="font-semibold text-gray-200">@{about}</span>
              <button onClick={() => setParams({})} data-testid="clear-about-filter" className="rounded-full p-1 hover:bg-white/10"><X className="h-4 w-4" /></button>
            </div>
          )}
          {threads.map((t, i) => <ThreadCard key={t.id} thread={t} index={i} />)}
          {threads.length === 0 && <p className="text-sm text-gray-500" data-testid="threads-empty">No discussions yet.</p>}
        </div>
        <aside className="h-fit rounded-md border border-[#2A2E37] bg-[#121418] p-5" data-testid="communities-list">
          <h2 className="font-display text-base font-semibold">All communities</h2>
          <div className="mt-4 space-y-1">
            {communities.map((c) => (
              <Link key={c.id} to={`/c/${c.slug}`} data-testid={`community-link-${c.slug}`}
                className="flex items-center justify-between rounded-sm px-3 py-2.5 transition-colors hover:bg-white/5">
                <span className="flex items-center gap-2 text-sm font-semibold"><Hash className="h-4 w-4 text-[#FF2E4D]" />{c.name}</span>
                <span className="text-xs text-gray-500">{c.threads_count}</span>
              </Link>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
